import * as React from 'react';
import PropTypes from 'prop-types';
import { Tooltip, Dropdown, Option } from '@fluentui/react-components';
import { FluentButton } from '../../../../../components/FluentUI/Fluent-Button';
import FluentUiIcon from '../../../../../assets/fluent-ui-icon';
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { getStaffingIndexColumns, handleJuniorStaffingIndexExport } from '../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log-helper';
import { exportImage, Group, geometry } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';
import { exportVisual } from '@progress/kendo-react-charts';

const FREQUENCY_OPTIONS = ['Weekly'];
const PERIOD_OPTIONS = [
    { value: 4, label: 'Past 4 Wks' },
    { value: 8, label: 'Past 8 Wks' },
    { value: 12, label: 'Past 12 Wks' },
    { value: 16, label: 'Past 16 Wks' },
];
const EnergyJuniorStaffingIndexHeader = ({
    frequency = 'Weekly',
    period,
    onPeriodChange,
    expandWidget,
    expandClass,
    chartRef,
    energyJuniorStaffingIndexData,
    showToast
}) => {
    const _staffingIndexExport = React.useRef(null);
    const handlePeriodSelect = (_e, data) => {
        onPeriodChange(Number(data.optionValue));
    };
    const selectedOption = PERIOD_OPTIONS.find((o) => o.value === period);
    const [open, setOpen] = React.useState(false);

    //Fluent Dropdown fix for scrolling to top on open, fixed in newer versions of Fluent, can remove when we update Fluent version
    const handleOpenChange = (_e, data) => {
        if (data.open) {
            const y = window.scrollY;
            // restore after Fluent's effect has had a chance to scroll
            requestAnimationFrame(() => window.scrollTo({ top: y }));
            requestAnimationFrame(() =>
                requestAnimationFrame(() => window.scrollTo({ top: y }))
            );
        }
        setOpen(data.open);
    };

    const onImageExportClick = React.useCallback(() => {
        if (!chartRef.current) return;
        const chartVisual = exportVisual(chartRef.current);
        if (!chartVisual) return;

        const scale = 2; // resolution multiplier; higher = sharper + larger file
        const bbox = chartVisual.bbox();

        const width = 2560 * scale;
        const height = 807 * scale;

        const group = new Group();
        group.append(chartVisual);
        group.transform(
            geometry.transform().scale(
                width / bbox.size.width,
                height / bbox.size.height
            )
        );

        exportImage(group, { width, height }).then(async (dataURI) => {
            const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
            saveAs(dataURI, `Energy_Junior_Staffing_Index_${date}.png`);

            try {
                const blob = await (await fetch(dataURI)).blob();
                await navigator.clipboard.write([
                    new ClipboardItem({ 'image/png': blob }),
                ]);
                showToast("An export of this chart was copied to your clipboard and downloaded");
            } catch {
                showToast("Chart downloaded as PNG (clipboard not available)", "", "warning");
            }
        });
    }, []);

    return (
        <div className="widget-header" data-testid="header">
            <div className="header-left">
                <div className="main-title-badge-row">
                    <div className="main-title">Junior Staffing Index</div>
                </div>
                <div className="sub-title">{'GIB/Energy'}</div>
            </div>
            <div className="header-right">
                <Dropdown
                    aria-label="Frequency"
                    value={frequency}
                    selectedOptions={[frequency]}
                    disabled={true}
                >
                    {FREQUENCY_OPTIONS.map((opt) => (
                        <Option key={opt} value={opt} text={opt}>
                            {opt}
                        </Option>
                    ))}
                </Dropdown>
                <Dropdown
                    value={selectedOption ? selectedOption.label : ''}
                    selectedOptions={[String(period)]}
                    onOptionSelect={handlePeriodSelect}
                    open={open}
                    onOpenChange={handleOpenChange}
                >
                    {PERIOD_OPTIONS.map((opt) => (
                        <Option key={opt.value} value={String(opt.value)} text={opt.label}>
                            {opt.label}
                        </Option>
                    ))}
                </Dropdown>
                <Tooltip content="Copy chart image to clipboard & download as PNG">
                    <span>
                        <FluentButton
                            appearance="secondaryWithOutline"
                            icon={
                                <FluentUiIcon
                                    name="ClipboardImageRegular"
                                    color="#0F6CBD"
                                    size={20}
                                />
                            }
                            onClick={onImageExportClick} />
                    </span>
                </Tooltip>
                <Tooltip content="Export Data">
                    <span>
                        <FluentButton
                            appearance="secondaryWithOutline"
                            icon={
                                <FluentUiIcon
                                    name="ArrowDownloadRegular"
                                    color="#0F6CBD"
                                    size={20}
                                />
                            }
                            onClick={() => handleJuniorStaffingIndexExport({ detailRef: _staffingIndexExport })}
                        />
                    </span>
                </Tooltip>
                <Tooltip content="Expand Widget">
                    <span>
                        <FluentButton
                            appearance="secondaryWithOutline"
                            icon={
                                <FluentUiIcon
                                    name={
                                        expandClass === 'widgetExpanded'
                                            ? 'FullScreenMinimizeFilled'
                                            : 'FullScreenMaximizeFilled'
                                    }
                                    color="#0F6CBD"
                                    size={20}
                                />
                            }
                            onClick={expandWidget}
                        />
                    </span>
                </Tooltip>
            </div>
            <ExcelExport data={energyJuniorStaffingIndexData?.ExportData} ref={_staffingIndexExport}>
                {getStaffingIndexColumns()}
            </ExcelExport>
        </div>
    );
};

EnergyJuniorStaffingIndexHeader.propTypes = {
    frequency: PropTypes.string,
    period: PropTypes.number,
    onPeriodChange: PropTypes.func,
    expandWidget: PropTypes.func,
    expandClass: PropTypes.string,
    energyJuniorStaffingIndexData: PropTypes.shape({
        ChartData: PropTypes.shape({
            Dates: PropTypes.array,
            EstimatedIndex: PropTypes.array,
            ReportedIndex: PropTypes.array,
        }),
        ExportData: PropTypes.array,
    }),
    chartRef: PropTypes.object,
    showToast: PropTypes.func,
};

export default EnergyJuniorStaffingIndexHeader;